import { z } from 'zod';

/**
 * How the money actually reached the platform.
 *
 * A **column, not a branch** — deliberately, and the reason is in
 * `03-payments-iap.md` §5: local gateways (Tap/Moyasar — Mada/STC Pay) are a
 * planned automation of the same flow, and when one arrives it becomes another
 * creator of `awaiting_confirmation` orders rather than a second order model.
 * Nothing in the state machine reads this value; it is what a staff member and
 * an auditor need in order to reconcile an order against a bank statement.
 *
 * - `bank_transfer` — a wire or app transfer, the common case in markets where
 *   store billing is unavailable.
 * - `cash` — paid at an office, receipted by hand.
 * - `gateway` — taken by a local payment gateway. Reserved now so the enum does
 *   not have to change on the day one is wired up.
 * - `other` — anything a deployment does that this list does not name, described
 *   in the order's note. It exists so a staff member never has to mislabel a
 *   payment to record it; U16 forbids assuming one market's payment habits.
 */
export const DIRECT_PAYMENT_METHODS = ['bank_transfer', 'cash', 'gateway', 'other'] as const;

export const DirectPaymentMethodSchema = z.enum(DIRECT_PAYMENT_METHODS);

export type DirectPaymentMethod = (typeof DIRECT_PAYMENT_METHODS)[number];

const METHOD_SET: ReadonlySet<string> = new Set<string>(DIRECT_PAYMENT_METHODS);

export function isDirectPaymentMethod(value: string): value is DirectPaymentMethod {
  return METHOD_SET.has(value);
}
